import DoneIcon from '@mui/icons-material/Done'
import { Box, Tooltip, Typography, useTheme } from '@mui/material'
import { ITask } from '../../definitions/Workflow'
import Formatter from '../../helpers/Formatter'
import Tile from '../layout/Tile'

interface P {
    tasks?: { task: ITask, startDate: Date, endDate: Date }[]
    taskMap: Map<string, ITask>
}

export default function TaskTimeline({ tasks: originalTasks, taskMap }: P) {
    const theme = useTheme()
    const tasks = originalTasks
    const iconSize = 30

    const timelineCard = (task: ITask, index: number, startDate: Date, endDate: Date) => {
        const completedAt = Formatter.timeAgo(new Date(task.completedAt))
        const taskColor = task.status === 'completed' ? theme.palette.primary.main : theme.palette.text.primary
        const taskIcon = task.status === 'completed'
            ? (<DoneIcon sx={{ color: taskColor }} />)
            : (
                    <Typography textAlign='center' variant='h6' fontSize={16}>
                        {index}
                    </Typography>
                )
        return (
            <Tile.Row key={'timelineCard' + task.uuid} style={{ alignItems: 'center' }}>
                <Tile.Column
                    style={{
                        height: '100%',
                        borderLeft: '1px solid',
                        maxWidth: 1,
                        borderColor: theme.palette.grey['300'],
                        position: 'relative',
                        marginLeft: theme.spacing(2)
                    }}>
                    <Box
                        sx={{
                            border: '2px solid',
                            borderColor: taskColor,
                            backgroundColor: theme.palette.background.default,
                            height: iconSize,
                            width: iconSize,
                            borderRadius: '100%',
                            position: 'absolute',
                            top: 'calc(50% - ' + iconSize / 2 + 'px)',
                            left: -(iconSize / 2),
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                        {taskIcon}
                    </Box>
                </Tile.Column>
                <Tile.Column
                    style={{
                        color: taskColor,
                        backgroundColor: theme.palette.background.default,
                        margin: theme.spacing(2),
                        marginLeft: theme.spacing(3),
                        border: '1px solid ' + theme.palette.grey['500'],
                        borderRadius: theme.spacing(1),
                        padding: theme.spacing(2),
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1
                    }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between'>
                        <Box>
                            <Typography
                                variant='body2'
                                sx={{ textDecoration: task.status === 'completed' && 'line-through' }}
                                fontWeight='bold'>
                                {task.title}
                            </Typography>
                            <Typography
                                variant='body2'
                                sx={{ textDecoration: task.status === 'completed' && 'line-through' }}
                                marginTop={1}>
                                {task.description}
                            </Typography>
                        </Box>
                    </Box>
                    <Tile.Row style={{ marginTop: theme.spacing(2), display: 'flex', alignItems: 'center' }}>
                        <Typography variant='caption' marginRight={1}>
                            {task.status === 'completed' ? 'Completed by' : 'Assigned to'}
                        </Typography>
                        {task?.assignee && (
                            <Tooltip key={'assignee' + task.uuid} title={task.assignee.name}>
                                <img
                                    src={task.assignee.image}
                                    style={{ height: 25, width: 25, borderRadius: '100%' }}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title='Completion Date'>
                            <Typography variant='caption' marginLeft={1}>
                                {task.completedAt ? completedAt : 'in progress'}
                            </Typography>
                        </Tooltip>
                    </Tile.Row>
                    {(task.status === 'open') && (
                        <Tile.Row style={{ marginTop: theme.spacing(1) }}>
                            <Typography variant='caption' marginRight={1}>
                                Start Date:
                            </Typography>
                            <Typography variant='caption' fontWeight='bold'>
                                {startDate.toDateString()}
                            </Typography>
                        </Tile.Row>
                    )}
                    {(task.status === 'open') && (
                        <Tile.Row>
                            <Typography variant='caption' marginRight={1}>
                                End Date:
                            </Typography>
                            <Typography variant='caption' fontWeight='bold'>
                                {endDate.toDateString()}
                            </Typography>
                        </Tile.Row>
                    )}
                    <Tile.Row>
                        <Typography variant='caption' marginRight={1}>
                            Days to Finish:
                        </Typography>
                        <Typography variant='caption' fontWeight='bold'>
                            {task.daysToComplete}
                        </Typography>
                    </Tile.Row>

                    {(task.status === 'completed') && (
                        <Tile.Row>
                            <Typography variant='caption' marginRight={1}>
                                Completed at:
                            </Typography>
                            <Typography variant='caption' fontWeight='bold'>
                                {(task.completedAt) ? (new Date(task.completedAt).toDateString()) : ''}
                            </Typography>
                        </Tile.Row>
                    )}
                    <Tile.Row>
                        <Typography variant='caption' marginRight={1}>
                            Depending on :
                        </Typography>
                        <Typography variant='caption' fontWeight='bold'>
                            {task.dependencies?.length
                                ? task.dependencies?.map(depId => {
                                    const depTask = taskMap.get(depId.uuid)
                                    return depTask ? depTask.title : 'Unknown Task'
                                }).join(', ')
                                : '_'}
                        </Typography>
                    </Tile.Row>
                </Tile.Column>
            </Tile.Row>
        )
    }

    return (
        <div style={{
            flex: 1,
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            marginTop: theme.spacing(1) }}>
            {tasks.map(({ task, startDate, endDate }, i) =>
                timelineCard(task, i + 1, startDate, endDate)
            )}
        </div>
    )
}
